import type { Matter, MatterStatuteOfLimitations } from "@/lib/types";
import dayjs from "dayjs";

export const getSortedMatterStatutesList = <
  T extends Partial<MatterStatuteOfLimitations>,
>(
  statutes: T[] = [],
) => {
  return [...statutes].sort((a, b) => {
    const aDate = dayjs(a.date);
    const bDate = dayjs(b.date);

    if (!aDate.isValid()) return 1;
    if (!bDate.isValid()) return -1;

    return aDate.diff(bDate);
  });
};

export const getPrimaryMatterStatuteFromList = <
  T extends Partial<MatterStatuteOfLimitations>,
>(
  statutes: T[] = [],
): T | undefined => {
  return getSortedMatterStatutesList(statutes)[0];
};

export const getSortedMatterStatutes = (matter: Matter) => {
  return getSortedMatterStatutesList(matter.statutesOfLimitations);
};

export const getPrimaryMatterStatute = (
  matter: Matter,
): Matter["statutesOfLimitations"][number] | undefined => {
  return getPrimaryMatterStatuteFromList(matter.statutesOfLimitations);
};

/** Soonest upcoming SOL; if all SOLs have passed, the most recently elapsed SOL. */
export const getNextMatterStatute = (
  matter: Matter,
): Matter["statutesOfLimitations"][number] | undefined => {
  const validStatutes = getSortedMatterStatutes(matter).filter((statute) =>
    dayjs(statute.date).isValid(),
  );
  const today = dayjs().startOf("day");
  const upcomingStatute = validStatutes.find(
    (statute) => !dayjs(statute.date).startOf("day").isBefore(today),
  );

  return upcomingStatute ?? validStatutes[validStatutes.length - 1];
};

/** Earliest confirmed SOL; if none are confirmed, earliest SOL overall. */
export const getEarliestConfirmedMatterStatuteFromList = <
  T extends Partial<MatterStatuteOfLimitations>,
>(
  statutes: T[] = [],
): T | undefined => {
  const confirmedStatutes = statutes.filter(
    (statute) => statute.confirmed === true,
  );

  if (confirmedStatutes.length > 0) {
    return getPrimaryMatterStatuteFromList(confirmedStatutes);
  }

  return getPrimaryMatterStatuteFromList(statutes);
};

export const getEarliestConfirmedMatterStatute = (
  matter: Matter,
): Matter["statutesOfLimitations"][number] | undefined => {
  return getEarliestConfirmedMatterStatuteFromList(
    matter.statutesOfLimitations,
  );
};
